import { ID_LENGTH } from './id'

/**
 * **Jemanden in einem Kommentar erwähnen** (Nils, 18.08.2026).
 *
 * *„In den Kommentaren soll man auch mit @ user markieren können."*
 *
 * ## Wie eine Erwähnung im Text aussieht
 *
 * ```
 * @[Nils Waning](person:01K2E7Q0S6NBJ4RZP3XQ1V9WMD)
 * ```
 *
 * Also **Name und Kennung zusammen** – nicht `@nwaning` und nicht `@Nils Waning`.
 *
 * - Ein nackter `@Name` ist **nicht auflösbar**: es gibt „Chris Schmidt" und „Chris
 *   Schmitt", und die Personenliste entsteht aus zwei Quellsystemen, in denen
 *   dieselbe Person unterschiedlich heißt. Wer geraten wird, bekommt die Nachricht
 *   nicht – oder ein Falscher bekommt sie.
 * - Ein `@kürzel` gibt es nicht: `person` hat keinen Anmeldenamen, nur `display_name`
 *   und optional eine Adresse. Einen zu erfinden hieße, ein zweites Namensschema zu
 *   pflegen, das mit jedem Import auseinanderlaufen kann.
 * - Der Name steht **mit** in der Zeichenkette, damit ein Text auch dort lesbar bleibt,
 *   wo diese Datei nicht läuft: in der Datenbank, in einer Mail, in einem GitLab-Issue.
 *   Er ist eine Kopie vom Zeitpunkt des Schreibens; verbindlich ist die Kennung.
 *
 * ## Warum keine Tabelle „Erwähnungen"
 *
 * Die Kennung steht im Text, und der Text ist die einzige Quelle, die beim Ändern
 * eines Kommentars mitwandert. Eine zweite Tabelle müsste bei jedem Schreiben,
 * Ändern und Löschen mitgepflegt werden – und die erste vergessene Stelle wäre eine
 * Erwähnung, die es gibt und die niemand sieht. Dieselbe Überlegung wie bei der
 * Abnahme-Notiz ([E-103](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * ## Was das Format aushält
 *
 * Der Markdown-Renderer kennt `[Text](Ziel)` und lässt Ziele durch, die kein
 * `http(s):` oder `mailto:` sind – **als Klartext**. Eine Erwähnung sieht deshalb
 * auch ohne eigene Behandlung wie „@Nils Waning" aus und nie wie eine kaputte
 * Auszeichnung. Der Chip ist die Kür, der Klartext der Rückfall.
 */

/** Crockford-Base32 wie in `id.ts` – ohne I, L, O, U. */
const KENNUNG = `[0-9A-HJKMNP-TV-Z]{${ID_LENGTH}}`

/**
 * Eine Erwähnung. Gruppe 1 ist der Name, Gruppe 2 die Kennung.
 *
 * Im Namen ist alles außer `]` erlaubt – Umlaute, Bindestriche, Punkte. `[` und `]`
 * werden beim Schreiben entfernt (siehe `erwaehnungSchreiben`), damit die Klammern
 * die Klammern bleiben.
 */
export const ERWAEHNUNG = new RegExp(`@\\[([^\\]\\n]{1,200})\\]\\(person:(${KENNUNG})\\)`)

const ERWAEHNUNG_GLOBAL = new RegExp(ERWAEHNUNG.source, 'g')

/**
 * Die Zeichenkette, die ins Textfeld eingesetzt wird.
 *
 * Der Name wird entschärft, nicht abgelehnt: eine Person heißt, wie sie im
 * Quellsystem heißt, und „Meier [extern]" ist ein Name, den es gibt.
 */
export function erwaehnungSchreiben(person: { id: string; displayName: string }): string {
  const name = person.displayName
    .replace(/[[\]\n]/g, ' ')
    .trim()
    .slice(0, 200)
  return `@[${name || 'unbekannt'}](person:${person.id})`
}

/**
 * Alle erwähnten Personen eines Textes, ohne Wiederholungen.
 *
 * Wer zweimal im selben Kommentar steht, ist einmal erwähnt – sonst zählte ein
 * Kommentar, der jemanden zweimal anspricht, doppelt.
 */
export function erwaehnteIds(text: string): string[] {
  const gefunden = new Set<string>()
  for (const treffer of text.matchAll(ERWAEHNUNG_GLOBAL)) {
    if (treffer[2]) gefunden.add(treffer[2])
  }
  return [...gefunden]
}

/** Steht diese Person im Text? */
export function erwaehnt(text: string, personId: string): boolean {
  return text.includes(`](person:${personId})`)
}

/**
 * **Erwähnungen auf die stutzen, die es geben darf.**
 *
 * Der Client schickt Text; was darin als Kennung steht, ist eine Behauptung. Der
 * Server entscheidet, welche Personen erreichbar sind (aktiv und anmeldbar), und
 * alles andere wird zu Klartext – der Kommentar bleibt stehen, die Erwähnung wird
 * zum Namen.
 *
 * **Abweisen wäre falsch.** Zwischen Tippen und Absenden kann jemand deaktiviert
 * werden; dann verlöre man den geschriebenen Kommentar für einen Umstand, der mit
 * ihm nichts zu tun hat. Der Name bleibt lesbar, nur angesprochen wird niemand.
 */
export function erwaehnungenBeschraenken(
  text: string,
  istErreichbar: (personId: string) => boolean,
): string {
  return text.replace(ERWAEHNUNG_GLOBAL, (treffer, name: string, id: string) =>
    istErreichbar(id) ? treffer : `@${name}`,
  )
}

/**
 * **Für alles, was nicht Work ist:** aus `@[Nils Waning](person:01K…)` wird
 * `@Nils Waning`.
 *
 * Gebraucht beim Rückschreiben nach GitLab und NetCon. Dort ist die Kennung
 * sinnlos – und `@nwaning` daraus zu machen wäre schlimmer als sinnlos: in GitLab
 * löst das eine Benachrichtigung aus, im NetCon-Ticket steht der Feed vor dem
 * Kunden. Work benachrichtigt in Work; was hinausgeht, ist Text.
 *
 * **Das `@` bleibt hier stehen, und es darf so nicht nach GitLab.** Der Rest-`@` reicht
 * dort aus, um einen Fremden zu treffen (21.08.2026) – wer nach GitLab schreibt, schickt
 * das Ergebnis deshalb zusätzlich durch `ansprachenEntschaerfen`. Für die Vorschau in der
 * Mail und im Feed ist `@Nils Waning` dagegen richtig: dort wertet niemand das Zeichen aus.
 */
export function erwaehnungenZuKlartext(text: string): string {
  return text.replace(ERWAEHNUNG_GLOBAL, (_treffer, name: string) => `@${name}`)
}

/**
 * **Kein `@` verlässt Work als Ansprache** (21.08.2026,
 * [E-202](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * ## Was passiert ist
 *
 * Work schrieb einen Kommentar nach GitLab, in dem `@[Christian Hunhoff](person:01K…)`
 * stand. `erwaehnungenZuKlartext` machte daraus `@Christian Hunhoff` – gedacht als
 * *Klartext*. GitLab liest aber jedes `@wort` selbst und macht daraus eine Erwähnung: aus
 * `@Christian` wurde **`@christian`, ein wildfremder Mensch** auf der GitLab-Instanz, der
 * eine Benachrichtigung über eine Aufgabe bekam, die ihn nichts angeht. Gemeldet von
 * Christian Hunhoff am 21.08.2026.
 *
 * „Klartext" ist also die falsche Vorstellung: In einem System, das `@` selbst auswertet,
 * ist `@` kein Satzzeichen, sondern ein Aufruf. Der Name allein reicht, um jemanden zu
 * treffen – GitLab braucht dafür kein Kürzel und keine Kennung, es rät.
 *
 * ## Warum das Zeichen weg muss und nicht bloß entschärft wird
 *
 * Naheliegend wären Backticks (`` `@Name` ``) oder das Backslash-Escape (`\@Name`), das
 * GitLab kennt. Beides hängt daran, dass ein fremder Markdown-Parser sich so verhält, wie
 * wir glauben – und trifft auf Text, der schon Backticks enthalten kann. Der Einsatz ist
 * eine Nachricht an einen Unbeteiligten; die Sicherung darf deshalb nicht von einer
 * Auslegung abhängen, sondern muss am Zeichen selbst hängen. **Ohne `@` gibt es nichts zu
 * parsen.**
 *
 * Aus `@Christian Hunhoff` wird `Christian Hunhoff`, aus `@all` wird `all`. Wer im Issue
 * mitliest, sieht den Namen – wer benachrichtigt werden soll, wird es in Work.
 *
 * Angefasst wird nur, was ein Ziel haben könnte: `@` **direkt vor** einem Wortzeichen und
 * **nicht hinter** einem. Eine Mailadresse (`nils@wani.ng`) bleibt deshalb stehen – sie ist
 * für GitLab ohnehin keine Ansprache –, ein alleinstehendes `@` im Satz auch.
 *
 * **Seit dem 21.08.2026 abends ist das der Sonderfall der leeren Liste** – wer angesprochen
 * werden *darf*, steht bei `ansprachenBeschraenken` und [E-204](../../../docs/ENTSCHEIDUNGEN.md).
 * Diese Fassung hier heißt: niemand.
 */
export function ansprachenEntschaerfen(text: string): string {
  return ansprachenBeschraenken(text, [])
}

/**
 * Was im Zielsystem eine Ansprache wäre: ein `@` am Wortanfang und das Wort dahinter.
 *
 * **Auch in seiner HTML-Schreibweise** (21.08.2026, Nils: *„sichere das alles gut genug ab"*).
 * `&#64;`, `&#x40;` und `&commat;` sind dasselbe Zeichen; ein Zielsystem, das Markdown zu HTML
 * macht und **danach** nach Erwähnungen sucht, sieht keinen Unterschied. Ob GitLab das
 * wirklich tut, ist ungeprüft – und genau deshalb steht es hier: die Sicherung soll nicht
 * davon abhängen, wie ein fremder Renderer arbeitet.
 *
 * Niemand tippt so etwas versehentlich. Der Preis ist also fast null, der Gewinn ist eine
 * Lücke weniger, über die man nachdenken muss.
 */
const AT = '(?:@|&#0*64;|&#[xX]0*40;|&commat;)'
const ANSPRACHE = new RegExp(`(^|[^\\w])(?:${AT})+(\\w[\\w.-]*)`, 'g')

/**
 * **Angesprochen wird, wer auf der Liste steht – der Rest verliert sein `@`**
 * (21.08.2026, [E-204](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Die Liste sind die Anmeldenamen, die Work über die **Zuordnung** kennt; sie entsteht dort,
 * wo geschrieben wird, und nicht aus dem Text. Damit ist die Regel an der letzten Stelle
 * prüfbar: *steht dieses `@wort` auf der Liste?* Alles andere – ein getipptes `@simone`, ein
 * `@all`, ein Name aus einem zitierten Absatz – geht ohne `@` hinaus.
 *
 * Ein Anmeldename darf Punkte tragen (`vorname.nachname`), ein Satz endet aber auch mit
 * einem. `@nwaning.` am Satzende wird deshalb bis zum passenden Namen zurückgeschnitten,
 * bevor die Liste befragt wird – und der Punkt bleibt Satzzeichen.
 */
export function ansprachenBeschraenken(text: string, erlaubt: readonly string[]): string {
  const menge = new Set(erlaubt.map((h) => h.trim().toLowerCase()).filter(Boolean))
  return text.replace(ANSPRACHE, (_treffer, vor: string, wort: string) => {
    let kern = wort
    while (kern && !menge.has(kern.toLowerCase()) && /[._-]$/.test(kern)) kern = kern.slice(0, -1)
    return menge.has(kern.toLowerCase()) ? `${vor}@${wort}` : `${vor}${wort}`
  })
}

/**
 * **Ein Anmeldename, wie ein Quellsystem ihn vergibt** – ein Wort, keine Lücke.
 *
 * `external_username` ist ein freies Feld; stünde dort je ein Anzeigename („Nils Waning"),
 * ergäbe `@Nils Waning` in GitLab genau den Fehler, gegen den diese Datei geschrieben ist –
 * angesprochen würde `@nils`. Was nicht wie ein Anmeldename aussieht, ist deshalb keiner.
 */
const HANDLE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,60}$/

/**
 * **Wer ein Konto im Zielsystem hat, wird dort angesprochen – und sonst niemand**
 * (Nils, 21.08.2026, [E-204](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * *„Wichtig, dass genau der GitLab-Name bei dem Entwickler bei uns hinterlegt ist. Es ist
 * nicht der Klartextname, die haben ja alle eigene GitLab-Namen. Wenn ich bspw. aus NetCon
 * eine Simone habe, dann dürfen wir auch nicht, wenn ich in einer GitLab-Aufgabe Simone
 * markiert habe in Work, irgendeiner Simone schreiben."*
 *
 * Das ist die Verschärfung von [E-202](../../../docs/ENTSCHEIDUNGEN.md), nicht deren
 * Rücknahme. Dort fiel jedes `@` weg, weil der **Name** kein Ziel ist. Hier kommt es an
 * genau einer Stelle zurück: wenn Work die Kennung des Menschen im Zielsystem **kennt**,
 * weil sie beim Import an dieser Person hängt.
 *
 * | in Work | im Zielsystem |
 * |---|---|
 * | Erwähnung, Person hat dort ein Konto | `@nwaning` – der hinterlegte Anmeldename |
 * | Erwähnung, Person hat dort keins | `Simone Vogt` – der Name, ohne `@` |
 * | von Hand getipptes `@simone` | `simone` – ohne `@` |
 *
 * **Die dritte Zeile ist die wichtigste.** Ein getipptes `@simone` ist eine *Vermutung*, und
 * die Kennung im Zielsystem entsteht nicht aus Vermutungen, sondern aus der Zuordnung, die
 * beim Import entstanden ist. Deshalb wird zuerst der ganze Text entschärft und **erst
 * danach** die Erwähnung gesetzt: was am Ende ein `@` trägt, ist ausschließlich durch die
 * Zuordnung dorthin gekommen. Die Reihenfolge geht auf, weil `ansprachenEntschaerfen` das
 * Work-Format nicht anfasst – dort folgt auf das `@` eine `[`.
 *
 * `handleFuer` gibt `null` zurück, wenn es kein Konto gibt **oder wenn es mehrere sind**:
 * Zwei Konten sind keine Auskunft, sondern eine Wahl, und die trifft hier niemand.
 */
export function erwaehnungenFuerFremdsystem(
  text: string,
  handleFuer: (personId: string) => string | null | undefined,
): string {
  return ansprachenEntschaerfen(text).replace(
    ERWAEHNUNG_GLOBAL,
    (_treffer, name: string, id: string) => {
      const handle = handleFuer(id)?.trim()
      return handle && HANDLE.test(handle) ? `@${handle}` : name
    },
  )
}

/**
 * Ein Konto im Quellsystem, das angesprochen werden darf.
 *
 * `handle` ist der Anmeldename dort (`person_external_id.external_username`) – ohne `@`.
 */
export interface FremdKonto {
  handle: string
  personId: string
  displayName: string
}

/** Was in einem Regex eine Bedeutung hat, verliert sie hier. `dhepcan.de` enthält einen Punkt. */
function maskieren(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')
}

/**
 * **Die Gegenrichtung zu `erwaehnungenZuKlartext`:** aus `@nwaning` wird
 * `@[Nils Waning](person:01K…)` (20.08.2026, [E-182](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Nils: *„aber nwaning bin doch ich, ist das durch die gitlab/user zuordnung nicht
 * rübergekommen? die gitlab usernamen können doch auch einmal verheiratet werden oder?"*
 *
 * Sie **war** verheiratet – seit dem ersten Import steht das Handle in
 * `person_external_id.external_username`, und beim Rückschreiben nach GitLab wird es
 * benutzt. Gefragt wurde es nur nie beim **Lesen**: der Text eines eingehenden
 * Kommentars ging unverändert ins Ereignis, und damit erkannte weder der Feed noch die
 * Hervorhebung, dass jemand angesprochen war. Gemessen am 20.08.2026: 325 Kommentare auf
 * 207 Aufgaben enthalten `@nwaning`, keiner davon war je eine Erwähnung.
 *
 * ## Warum aus einer Liste und nicht aus einem Muster
 *
 * Ein `@[\w.-]+` fände auch `@Tom` und `@Nils` – zwei von Hand getippte Vornamen, die
 * es in den Kommentaren wirklich gibt und die zu **keinem** Konto gehören. Wer die
 * auflösen will, muss raten, und das ist genau der Fehler, den das Work-eigene Format
 * vermeidet (siehe oben: „Chris Schmidt" und „Chris Schmitt"). Erkannt wird deshalb nur,
 * was in `konten` steht; alles andere bleibt Text.
 *
 * Die Liste ist **nach Länge sortiert**, damit `@dhepcan.de` nicht als `@dhepcan` mit
 * einem angehängten `.de` gelesen wird – das längste passende Konto gewinnt.
 *
 * ## Wo die Grenzen liegen
 *
 * - **Vor dem `@` darf kein Wortzeichen stehen**, sonst würde aus `nils@nwaning.de` eine
 *   Erwähnung. Eine Mailadresse ist keine Ansprache.
 * - **Dahinter darf keiner folgen**, und ein Punkt nur dann, wenn danach kein Buchstabe
 *   kommt: `@nwaning.` am Satzende ist eine Erwähnung, `@nwaning.beispiel` nicht.
 * - **Code wird nicht ausgenommen.** Ein `@nwaning` in einem Codeblock würde umgeschrieben.
 *   Das ist bewusst in Kauf genommen: der Markdown-Renderer zeigt die Erwähnung als
 *   Klartext `@Nils Waning`, also bleibt der Block lesbar – und ein halber
 *   Markdown-Parser, nur um diesen Fall zu treffen, wäre mehr Fehlerquelle als Gewinn.
 */
export function fremdErwaehnungenAufloesen(text: string, konten: readonly FremdKonto[]): string {
  if (konten.length === 0 || !text.includes('@')) return text

  const nachLaenge = [...konten].sort((a, b) => b.handle.length - a.handle.length)
  const muster = new RegExp(
    `(?<![\\w@])@(${nachLaenge.map((k) => maskieren(k.handle)).join('|')})(?![\\w-])(?!\\.[A-Za-z0-9])`,
    'gi',
  )

  return text.replace(muster, (_treffer, handle: string) => {
    const konto = nachLaenge.find((k) => k.handle.toLowerCase() === handle.toLowerCase())
    return konto
      ? erwaehnungSchreiben({ id: konto.personId, displayName: konto.displayName })
      : `@${handle}`
  })
}
